// ForcedReadGate.jsx — SB-146 站内信强读闸 + 直登清欠链（前端半，第一百二十二批）。
//
// ── 两段一只件 ──────────────────────────────────────────────────────────────
//  · 强读段（phase 'dm'）：有未读站内信 ⇒ 全屏遮罩压过一切路由，旧→新逐封
//    peek 取体呈现 → 滚动到底（**短正文视同已到底**）→ 3 秒可见读秒 → POST read
//    → 下一封；全清自动放行。对**所有登录用户**生效，无豁免（裁决 2）。
//  · 清欠段（phase 'notif'，仅 sessionOrigin==='login_link'）：站内信清完后，
//    铃铛未读旧→新逐条跳到 notifTarget 的目标页 + 缘由 modal + 标已读；
//    尝试离开 ⇒ 跳下一条；全清 ⇒ modal 告知恢复自由（裁决 4）。
//
// ── 需求方 2026-08-20 裁：推翻 SB-146 裁①（自动标已读 → 手动确认）────────────
// 原裁「不设确认键，倒计时归零即自动标已读并自动前进」。推翻后：读秒归零只把
// 「确认已读」点亮，**人不点就永远不标**。理由是自动前进把「读完」这件事的主语
// 从人换成了计时器——一个挂机三分钟回来的人，服务端记录他读完了七封信、撤回窗口
// 全部关死，而他一个字没看。手动确认是这条链上唯一一处人真的出场的地方。
// 接线纪律：标已读只许挂在 onClick 上（判据按行咬），任何「remain===0 就调它」
// 的 effect 都是这条裁决的回退。
//
// ── 一轮读完 ≠ 未读清空（第一百二十五批修的静默缺陷）──────────────────────
// 收件箱/通知查询都有 limit，闸此前在「本轮队列走完」那一刻直接放行，未读多于
// 一页时第 51 封起被静默宣布不存在。两段现在同走 drainRounds（forcedReadQueue.mjs）：
// 走完一轮就再拉一轮，**拉回 0 条**才是放行的唯一依据。收件箱那半还多一步——
// 它的列表是「最新 N 行、不分已读未读」，读过的信不会离开窗口，所以本页零未读时
// 还要问服务端的 unreadCount，欠着就把窗口开到最大再取（fetchForcedRound 头注写了
// 完整的证伪过程）。
//
// ── 触发纪律（裁决 3 + §1 边界）────────────────────────────────────────────
// 只在**路由切换**时查未读：AppShell 初次挂载（登录/刷新落地即算一次跳转）与每次
// location 变更；页内交互一概不查也不拦——正在填表的人不该被一封刚到的信打断，
// 他会在下一次跳转时被拦住。查询带短 TTL（forcedReadQueue.shouldRecheck），
// 连点导航不产生查询风暴；铃铛自己的 60s 轮询保持原样，这里不碰它。
//
// ── 为什么是「遮罩盖住新页」而不是「阻止导航」───────────────────────────────
// 本仓是普通 BrowserRouter（无 data router），真正拦截 navigation 需要 useBlocker
// 一族。遮罩全屏、压过一切路由（MUI Dialog 的 modal 层高于 AppBar），效果等同：
// 目标路由在遮罩下面等着，全清那一刻自动露出——这正是「放行原目标路由」。
//
// ── 判定全部在 .mjs（SB-042）────────────────────────────────────────────────
// 排序/到底/倒计时在 forcedReadQueue.mjs，链构建/推进/完成在 cleanupChain.mjs，
// 本文件只做量（DOM 尺寸）、调接口和渲染。第一百零八批 R1 的教训：判定写在
// .jsx 里，删掉它全套测试照样绿。
//
// ── 正文取体走 peek，绝不走 openMessage ─────────────────────────────────────
// openMessage 的语义是「打开即已读」；强读闸若用它取体，遮罩一弹出这封信就已读了
// ——闸变成空转的仪式，排队机制照样欠着。peek=1 不标已读（契约 §3.2），已读只由
// 「到底 + 读秒完成」那一刻的 POST read 写下。判据在 forcedReadQueue.test.mjs
// 里读本文件源码咬住（openMessage 不许在此出现）。
//
// ── read 失败不静默吞 ───────────────────────────────────────────────────────
// 标已读失败 ⇒ 当面说、给重试，**不跳过这封**：跳过等于把「保证被读到」的闸
// 自己打了个洞——这封信下次还会拦，但用户此刻以为自己读完了。
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Stack, Typography,
} from '@mui/material';
import api from '../api/client.mjs';
import { useAuth } from '../auth/AuthContext.jsx';
import { useLang, useT } from '../i18n/index.mjs';
import MarkdownBody from './MarkdownBody.jsx';
import ResponseDeadlineBar from './ResponseDeadlineBar.jsx';
import { fmtTime } from './TicketDetailView.jsx';
import { anchorLabelKey, anchorRefMs } from '../pages/messages.mjs';
import {
  READ_COUNTDOWN_SECONDS,
  drainRounds, fetchForcedRound, isAtBottom, mayConfirmRead, shouldRecheck, startCountdown,
} from './forcedReadQueue.mjs';
import {
  NOTIF_UNREAD_PAGE_LIMIT, buildChain, isDone, leftStep, needsCleanup,
} from './cleanupChain.mjs';

export default function ForcedReadGate() {
  const t = useT();
  const { lang } = useLang();
  const { sessionOrigin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // idle | dm（强读遮罩开着）| notif（清欠链走着）| freedom（全清告知）
  const [phase, setPhase] = useState('idle');

  // ── 强读段状态 ────────────────────────────────────────────────────────────
  const [queue, setQueue] = useState([]);       // 未读信，旧→新（buildForcedQueue）
  const [qIdx, setQIdx] = useState(0);
  const [peek, setPeek] = useState(null);       // { body, bodyFormat } | null=加载中
  const [peekNonce, setPeekNonce] = useState(0);
  const [peekErr, setPeekErr] = useState(false);
  const [bottom, setBottom] = useState(false);
  const [remain, setRemain] = useState(null);   // null=读秒未开始；3..0 界面可见
  const [readErr, setReadErr] = useState(false);

  // ── 清欠段状态（会话内存，刻意不落 localStorage——服务端未读数是唯一事实，
  //    刷新后按现状重建） ──────────────────────────────────────────────────
  const [chain, setChain] = useState([]);
  const [cIdx, setCIdx] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [stepMarked, setStepMarked] = useState(false);
  const [markErr, setMarkErr] = useState(false);
  const [markStuck, setMarkStuck] = useState(false); // 标已读连败 ≥3 ⇒ 开暂缓出口

  // 清欠只在**落地**武装一次（裁决 4 说的是直登那一刻的欠账；会话中新到的通知
  // 走铃铛的常规路径）。useRef 初值在首次渲染求值——首次渲染恰是落地。
  const cleanupArmedRef = useRef(sessionOrigin === 'login_link');
  const lastCheckRef = useRef(null);            // 上次未读查询的时刻（TTL 节流）
  const markFailRef = useRef(0);                // 清欠标已读连败计数（≥3 开暂缓出口）
  const scrollRef = useRef(null);
  // ── 穷尽读的三只把手 ──────────────────────────────────────────────────────
  // roundDone / chainDone：当前这一轮的 resolve——**人**把这一轮逐封点完时才调，
  // 驱动器于是去拉下一轮。它们是「界面进度」与「收敛保证」之间唯一的接缝。
  const roundDoneRef = useRef(null);
  const chainDoneRef = useRef(null);
  // 同一时刻只许一条驱动在跑：路由连点/StrictMode 双跑会让两条驱动互相覆盖队列，
  // 那是「刚点完确认的信又被拦一遍」的形状。
  const drainingRef = useRef(false);
  const confirmingRef = useRef(false);          // 确认键在途：双击不许跳过一封

  const cur = phase === 'dm' ? (queue[qIdx] ?? null) : null;
  const step = phase === 'notif' ? (chain[cIdx] ?? null) : null;

  // ── 清欠段收摊：把链状态与 phase 一起收干净 ───────────────────────────────
  // **phase 必须跟着收**（第一百二十六批证伪的静默锁死）：gotoStep 走完本轮时故意
  // 不动 phase（遮罩要留着等下一轮），于是「第一轮清完 → 拉第二轮 → 网络抖动被拒」
  // 那条路上，若拒绝分支只清 chain 不收 phase，phase 就永停在 'notif'——而触发
  // effect 的第一句是 `if (phase !== 'idle') return;`，此后本会话新到的未读站内信
  // 一封都不会被拦，界面上却什么都不显示：一道自称保证送达的闸，无声地停摆。
  // 所以「链撤下」这件事只许有一处写法，凡是撤链的分支都调它。
  // phase 用函数式收：只把 'notif' 收回 'idle'，'dm'/'freedom' 原样留着——
  // 这条链在别的 phase 上撤下时（不入链、暂缓后重入）不该顺手改掉别人的状态。
  const collapseChain = () => {
    setChain([]);
    setCIdx(0);
    setModalOpen(false);
    setPhase((p) => (p === 'notif' ? 'idle' : p));
  };

  // ── 清欠链启动（站内信清完之后才走到这里）─────────────────────────────────
  // 与强读段同走穷尽驱动：一轮链走完只说明这一页的未读通知清完了，limit 之外的
  // 还欠着——拉回 0 条才是「恢复自由」那句话的依据。
  const beginNotifChain = () => {
    drainRounds(
      () => api.notifications({ unread: true, limit: NOTIF_UNREAD_PAGE_LIMIT })
        .then((r) => buildChain(r?.notifications)),
      (steps) => new Promise((resolve) => {
        // 入链判定走裁决 4 的谓词原文：login_link 且有欠账。站内信在此刻恒为 0
        // （强读段刚清完或本来没有），所以只问铃铛。
        // 不该清欠 ⇒ 以「主动中止」收场，而不是让驱动继续拉：那不是清完了。
        if (!needsCleanup(sessionOrigin, 0, steps.length)) {
          resolve(false);
          return;
        }
        chainDoneRef.current = resolve;
        setChain(steps);
        setCIdx(0);
        setPhase('notif');
      }),
    ).then(
      ({ rounds, stopped }) => {
        chainDoneRef.current = null;
        // 「暂缓」/不入链：各自已把界面收好，这里再收一次也只是幂等——但**必须收**，
        // 因为「已经收好了」是一句关于别处的断言，而 phase 停在 'notif' 就是全站停摆。
        if (stopped) { collapseChain(); return; }
        collapseChain();
        // rounds > 1 ⇒ 至少真清完过一轮欠账 ⇒ 告知恢复自由（裁决 4 的收尾那一句）；
        // rounds === 1 ⇒ 第一次拉就是 0 条，本来无债可清，什么都不该弹。
        // 排在 collapseChain 之后：同一批次里后写的 setPhase 生效（'freedom' 压过它收回的 'idle'）。
        setPhase(rounds > 1 ? 'freedom' : 'idle');
      },
      () => {
        // 拉不到未读清单 ⇒ 本次落地不清欠：不装作清完，也不把网络抖动变成锁死。
        // 欠账原样躺在服务端，下一次直登/刷新按现状重建。
        // **收 phase**（collapseChain）：本轮链已经走完、chain 已清空，若只把 resolve
        // 置空就退出，phase 永停 'notif' ⇒ 触发 effect 首句早退 ⇒ 本会话此后新到的
        // 未读站内信一封都不会被拦，且没有任何东西出声。
        chainDoneRef.current = null;
        collapseChain();
      },
    );
  };

  // ── 强读段放行：把逐封状态与队列一起收干净 ────────────────────────────────
  // 只在驱动器收工时调（拉回 0 条 / 拉不动）——「本轮走完」不是放行的理由。
  const releaseGate = () => {
    roundDoneRef.current = null;
    setQueue([]);
    setQIdx(0);
    setPeek(null);
    setBottom(false);
    setRemain(null);
    setPhase((p) => (p === 'dm' ? 'idle' : p));
  };

  // ── 强读段的穷尽驱动：拉一轮 → 人逐封确认读完 → 再拉，直到拉回 0 封 ────────
  const runForcedGate = () => {
    if (drainingRef.current) return;
    drainingRef.current = true;
    drainRounds(
      // 取数编排（含「本页零未读但服务端还欠着 ⇒ 把窗口开到最大再问一次」）在
      // .mjs 里，组件只把 api 递进去——收件箱是 id DESC 取前 N 行、读过的信不会
      // 离开窗口，所以那一步不是优化而是正确性的一半（见 fetchForcedRound 头注）。
      () => fetchForcedRound((limit, before) =>
        api.messages({ box: 'inbox', limit, before })),
      (q) => new Promise((resolve) => {
        roundDoneRef.current = resolve;
        setQueue(q);
        setQIdx(0);
        setPhase('dm');
      }),
    ).then(
      () => {
        // 拉回 0 封 ⇒ 未读真清空 ⇒ 放行（遮罩落下，原目标路由就在下面）；
        // 直登落地还欠着铃铛的账则接链。
        releaseGate();
        if (cleanupArmedRef.current) {
          cleanupArmedRef.current = false;
          beginNotifChain();
        }
      },
      () => {
        // 某一轮拉不到（网络抖动）：不锁死界面（那是把一次抖动变成全站不可用），
        // 也不假装清完——清欠仍武装着，欠账原样躺在服务端，TTL 一过下次路由重来。
        releaseGate();
      },
    ).then(() => { drainingRef.current = false; });
  };

  // ── 触发：落地 + 每次路由切换（location.key 每次导航都变，含首次挂载）──────
  useEffect(() => {
    if (phase !== 'idle') return;                // 闸开着/链走着时路由变更另有含义
    if (drainingRef.current) return;             // 驱动在跑（比如正在拉下一轮）
    if (!shouldRecheck(Date.now(), lastCheckRef.current)) return;
    lastCheckRef.current = Date.now();
    runForcedGate();
    // 只认路由变更这一个触发（裁决 3 + §1：页内交互不拦）。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // ── 强读段：peek 取当前这封的正文 ─────────────────────────────────────────
  useEffect(() => {
    if (!cur) return undefined;
    setPeek(null);
    setPeekErr(false);
    setBottom(false);
    setRemain(null);
    setReadErr(false);
    let live = true;
    api.peekMessage(cur.id).then(
      (r) => {
        if (!live) return;
        // 带上信 id：mayConfirmRead 按 id 键控「正文真呈现过这一封」，不依赖重置时序
        //（must-fix 治本的第三层，见 forcedReadQueue.mjs 门上注释）。
        setPeek({ id: cur.id, body: typeof r?.body === 'string' ? r.body : '', bodyFormat: r?.bodyFormat });
      },
      () => { if (live) setPeekErr(true); },     // 取不到体 ⇒ 呈现 + 重试，不跳过
    );
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cur?.id, peekNonce]);

  // 到底判定：量在这里（DOM 读数），判在 isAtBottom（纯函数，含「不满一屏视同
  // 已到底」与 2px 容差）。
  const measure = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (isAtBottom({
      scrollTop: el.scrollTop, clientHeight: el.clientHeight, scrollHeight: el.scrollHeight,
    })) setBottom(true);
  }, []);

  // 正文一渲染就量一次（**短正文没有 scroll 事件可等**——不量它就永远到不了底）；
  // 之后 500ms 补量兜底：字体加载/窗口缩放会改 scrollHeight，而那些时刻没有事件
  // 落在这个容器上。onScroll 是主路径（见渲染段）。
  useEffect(() => {
    if (!cur || !peek || bottom) return undefined;
    const raf = requestAnimationFrame(measure);
    const h = setInterval(measure, 500);
    return () => { cancelAnimationFrame(raf); clearInterval(h); };
  }, [cur?.id, peek, bottom, measure]);

  // 到底 ⇒ 起 3 秒读秒（界面可见）。cleanup 即取消：换下一封时上一封的读秒必须停。
  useEffect(() => {
    if (!cur || !bottom) return undefined;
    return startCountdown(setRemain);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cur?.id, bottom]);

  const advanceQueue = () => {
    // 与 setQIdx 同一批次里同步重置逐封状态（must-fix 治本第一层）：不重置的话，
    // 下一封的首帧里 bottom=true/remain=0 还是上一封的值，确认键会在正文上屏前
    // 就亮着——一次误点即跳过一封（自动标已读的年代里那是当场静默标掉）。
    // peek 也一并清：过渡帧不许把旧正文错配到新发件人名下。
    setBottom(false);
    setRemain(null);
    setPeek(null);
    const next = qIdx + 1;
    if (next < queue.length) {
      setQIdx(next);
      return;
    }
    // 本轮走完 ≠ 未读清空：收件箱查询有 limit，此处直接放行正是那条静默缺陷。
    // 把 qIdx 推过队尾（cur ⇒ null，遮罩留着显示「正在检查」），再把这一轮交回
    // 穷尽驱动：它去拉下一轮，拉回 0 封才由 releaseGate 放行。
    setQIdx(next);
    const done = roundDoneRef.current;
    roundDoneRef.current = null;
    // 驱动不在（理论上不该发生）⇒ 宁可放行也不把人锁在一张空遮罩后面。
    if (done) done();
    else releaseGate();
  };

  const doRead = async (row) => {
    if (confirmingRef.current) return;            // 双击不许连推两封
    confirmingRef.current = true;
    setReadErr(false);
    try {
      await api.markMessageRead(row.id);
      advanceQueue();
    } catch {
      // 呈现并可重试，不跳过这封：跳过等于闸上开洞（头注）。幂等端点，重试安全。
      setReadErr(true);
    } finally {
      confirmingRef.current = false;
    }
  };

  // 「可以确认了吗」的门（四条件全在 .mjs）。它只决定按钮亮不亮——**标已读由
  // 点击那一下发起**，没有任何 effect 会替人点（2026-08-20 裁）。
  const mayConfirm = cur != null && mayConfirmRead(bottom, remain, peek?.id, cur.id);

  // ── 清欠段：进入第 cIdx 条——跳目标页 + 开缘由 modal + 标已读 ─────────────
  const markStep = async (s) => {
    setMarkErr(false);
    try {
      await api.markNotificationRead(s.id);
      setStepMarked(true);
      markFailRef.current = 0;
    } catch {
      setMarkErr(true);                           // 不静默：没标成不许推进（fail-closed）
      // 连败计数（证伪评审：标已读**持续**失败时全屏 modal 无退出路径=锁死整站）。
      // 三败之后开一条诚实的暂缓出口——欠账保留、链撤下，下次直登/刷新按服务端
      // 现状重建再清。锁死一个好意用户不是 fail-closed，是把闸变成故障。
      markFailRef.current += 1;
      if (markFailRef.current >= 3) setMarkStuck(true);
    }
  };

  const deferCleanup = () => {
    // 暂缓 ≠ 清完：不碰任何已读状态，只撤下本次链。modal 文案已如实告知欠账仍在。
    collapseChain();                              // 链与 phase 一起收（唯一一处写法）
    setMarkStuck(false);
    markFailRef.current = 0;
    // 主动中止穷尽驱动（resolve(false)）：绝不许它接着拉下一轮，更不许最后弹出
    // 「已全部清完，恢复自由」——欠账还整整齐齐地躺在服务端。
    const done = chainDoneRef.current;
    chainDoneRef.current = null;
    if (done) done(false);
  };

  useEffect(() => {
    if (!step) return;
    setModalOpen(true);
    setStepMarked(false);
    setMarkErr(false);
    setMarkStuck(false);
    markFailRef.current = 0;                      // 连败计数按条清零，不跨条累计
    // 终局类通知 target 为 null（notifTarget 的裁定：没有任何面还能显示它的对象）
    // ——无处可跳，但仍要标已读，否则未读数永不清零、下次直登永远重进清欠。
    if (step.target != null) navigate(step.target);
    markStep(step);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, step?.id]);

  const gotoStep = (next) => {
    if (isDone(chain, next)) {
      // 本轮链走完 ≠ 未读通知清空（notifications 查询同样有 limit）：交回穷尽
      // 驱动再拉一轮，拉回 0 条才由它弹「恢复自由」（裁决 4 的收尾那一句）。
      setChain([]);
      setCIdx(0);
      setModalOpen(false);
      const done = chainDoneRef.current;
      chainDoneRef.current = null;
      if (done) done();
      else setPhase('idle');
      return;
    }
    setCIdx(next);
  };

  const onModalOk = () => {
    setModalOpen(false);
    const next = cIdx + 1;
    // 无目标页的一条无从「离开」⇒ 确认即推进；最后一条读完即全清。
    if (step?.target == null || isDone(chain, next)) gotoStep(next);
  };

  // 用户尝试离开当前通知页（路由变更）⇒ 自动跳下一条并再示 modal。
  // modal 开着（含标已读失败重试中）或这条还没标成 ⇒ 不推进：fail-closed 的方向
  // 是链停在欠账上，绝不带着一条没标掉的通知宣布全清。
  useEffect(() => {
    if (phase !== 'notif' || !step) return;
    if (modalOpen || !stepMarked) return;
    if (!leftStep(location.pathname, step)) return;
    gotoStep(cIdx + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // 锚点那一行（与 MessagesPage.anchorText 同形；reply 用时刻不用裸 id——
  // 收件人在任何界面都查不到一个裸消息 id）。
  const anchorLine = (row) => {
    const key = anchorLabelKey(row.anchorKind);
    const ms = anchorRefMs(row);
    const ref = ms != null ? fmtTime(ms, lang) : (row.anchorLabel || row.anchorRef);
    return key ? t(key, { ref }) : row.anchorRef;
  };

  return (
    <>
      {/* ── 强读遮罩：全屏、无退出路径（无 onClose、Esc 关闭禁用）。放行只有
          一条路：读完并逐封确认。MUI Dialog 的 modal 层压过 AppBar 与一切路由内容。
          open 按 phase 而不按 cur：本轮走完、正在拉下一轮的那几百毫秒里 cur 是
          null，遮罩必须**留着**——落下再升起既是闪屏，也是半秒钟的可乘之机。 ── */}
      <Dialog open={phase === 'dm'} fullScreen disableEscapeKeyDown>
        {cur ? (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {t('forcedRead.title')}
              <Chip
                size="small"
                color="info"
                label={t('forcedRead.progress', { i: qIdx + 1, n: queue.length })}
              />
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {/* 秒数从闸的常量插值，绝不手写进文案：常量一改，手写的那句
                    当场变成假话（SB-151 的欠账，本批还清）。 */}
                {t('forcedRead.why', { s: READ_COUNTDOWN_SECONDS })}
              </Typography>
              <Stack
                direction="row"
                spacing={1.5}
                useFlexGap
                sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {t('dm.dir.in', { who: cur.counterpartDisplay || '—' })}
                </Typography>
                {/* SB-148 B：系统信标记——被闸拦下的人更需要知道这封不是某个人
                    手写的（模板信绕排队，最常在这道闸里第一次被读到）。 */}
                {cur.system ? (
                  <Chip size="small" color="info" variant="outlined"
                        label={t('dm.system.chip')} />
                ) : null}
                {/* SB-190 §3.3：要求响应——这道闸常是收件人第一次读到它的地方，
                    「这封要答」必须在他滚到底之前就看见。 */}
                {cur.responseRequired ? (
                  <Chip size="small" color="warning" variant="outlined"
                        label={t('dm.responseRequired.chip')} />
                ) : null}
                {/* SB-190 §8 步 5（第二百四十七批）：钟点条。闸拦下的信恒已发出（收件箱只有
                    sent_unread），所以带标记的行在这里必有钟——「几时前要答」要与「这封要答」
                    同一眼看见。行经 normalizeMessages（forcedReadQueue）而来，字段名同列表。 */}
                <ResponseDeadlineBar deadline={cur.responseDeadline} />
                <Typography variant="body2" color="text.secondary">
                  {anchorLine(cur)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('forcedRead.sentAt', { t: fmtTime(cur.sentAtMs ?? cur.createdAtMs, lang) })}
                </Typography>
              </Stack>
              {/* 滚动容器：onScroll 是到底判定的主路径；minHeight:0 让 flex 子项
                  真的可滚（否则内容把容器撑开、永远没有滚动条也永远到不了底）。 */}
              <Box
                ref={scrollRef}
                onScroll={measure}
                sx={{
                  flexGrow: 1, minHeight: 0, overflowY: 'auto',
                  border: 1, borderColor: 'divider', borderRadius: 1, p: 2, my: 1.5,
                }}
              >
                {peekErr ? (
                  <Alert
                    severity="error"
                    action={
                      <Button color="inherit" size="small"
                              onClick={() => setPeekNonce((n) => n + 1)}>
                        {t('common.retry')}
                      </Button>
                    }
                  >
                    {t('forcedRead.peekErr')}
                  </Alert>
                ) : !peek ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <MarkdownBody body={peek.body} format={peek.bodyFormat} />
                )}
              </Box>
              {/* 状态行：出错（可重试）> 「还没到底」提示。读秒本身长在按钮上，
                  不再另起一行说「几秒后自动标为已读」——那句话现在是假的。 */}
              {readErr ? (
                <Alert
                  severity="error"
                  action={
                    <Button color="inherit" size="small" onClick={() => doRead(cur)}>
                      {t('common.retry')}
                    </Button>
                  }
                >
                  {t('forcedRead.readErr')}
                </Alert>
              ) : peek && remain == null ? (
                <Alert severity="info" variant="outlined">{t('forcedRead.scrollHint')}</Alert>
              ) : null}
            </DialogContent>
            {/* ── 确认已读：这道闸上唯一由**人**按下的地方（2026-08-20 裁）。
                四条件不齐 ⇒ disabled（门在 .mjs 的 mayConfirmRead）；读秒未走完时按钮上
                显示剩余秒数，让「还要等」这件事就长在人要点的那颗键上。 ── */}
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                variant="contained"
                size="large"
                disabled={!mayConfirm}
                onClick={() => doRead(cur)}
              >
                {remain != null && remain > 0
                  ? t('forcedRead.countdown', { s: remain })
                  : t('forcedRead.confirm')}
              </Button>
            </DialogActions>
          </>
        ) : (
          // 本轮走完、正在拉下一轮：遮罩留着（未读是否真清空还没有答案），
          // 明说在做什么——一张沉默的空白全屏遮罩比任何错误提示都吓人。
          <DialogContent sx={{ display: 'flex', flexDirection: 'column',
                               alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              {t('forcedRead.checking')}
            </Typography>
          </DialogContent>
        )}
      </Dialog>

      {/* ── 清欠缘由 modal：必须解释**为什么页面会自己跳**（§3.6——第一次遇到的人
          不该以为这是 bug）。「知道了」在标已读成功前禁用：没标成不许推进。 ── */}
      <Dialog open={Boolean(step) && modalOpen} disableEscapeKeyDown>
        <DialogTitle>{t('cleanup.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('cleanup.reason', { i: cIdx + 1, n: chain.length })}
          </DialogContentText>
          <DialogContentText sx={{ mt: 1.5 }}>{t('cleanup.why')}</DialogContentText>
          {markErr && step ? (
            <Alert
              severity="error"
              sx={{ mt: 1.5 }}
              action={
                <Button color="inherit" size="small" onClick={() => markStep(step)}>
                  {t('common.retry')}
                </Button>
              }
            >
              {t('cleanup.markErr')}
            </Alert>
          ) : null}
          {markStuck ? (
            <DialogContentText sx={{ mt: 1.5 }}>{t('cleanup.deferNote')}</DialogContentText>
          ) : null}
        </DialogContent>
        <DialogActions>
          {markStuck ? (
            <Button onClick={deferCleanup}>{t('cleanup.deferBtn')}</Button>
          ) : null}
          <Button variant="contained" disabled={!stepMarked} onClick={onModalOk}>
            {t('cleanup.ok')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── 全清 ⇒ 恢复自由（裁决 4 的收尾）。这一个可以随手关。 ── */}
      <Dialog open={phase === 'freedom'} onClose={() => setPhase('idle')}>
        <DialogTitle>{t('cleanup.done.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('cleanup.done.body')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setPhase('idle')}>{t('cleanup.ok')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
